import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import type { EventoOrden, Incidencia, LineaOrden, OrdenDetalle } from '@manejo-alcanza/contracts';
import {
  anularOrden,
  cantidad,
  conUnidad,
  descargarArchivo,
  detalleOrden,
  fecha,
  pesos,
  resolverIncidencia,
} from '../api';
import { PanelAgenda } from '../componentes/Agenda';
import { Aviso, Boton, Card, Cargando, Error_, NoSe, Pill, Tabla } from '../componentes/basicos';
import { PanelDevoluciones } from '../componentes/Devoluciones';
import { PanelRecepcion } from '../componentes/Recepcion';

/**
 * El expediente. La pantalla más importante del sistema.
 *
 * Si esta pantalla está bien hecha, el resto es accesorio: es donde se
 * responde «¿qué pasó con esta orden?» sin abrir otros cuatro sistemas.
 */
export function DetalleOrden(): ReactNode {
  const { id = '' } = useParams();
  const cliente = useQueryClient();

  const consulta = useQuery({
    queryKey: ['orden', id],
    queryFn: () => detalleOrden(id),
  });

  const anular = useMutation({
    mutationFn: (motivo: string) => anularOrden(id, motivo),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['orden', id] }),
  });

  if (consulta.isLoading) return <Cargando que="el expediente" />;
  if (consulta.isError) return <Error_>{(consulta.error as Error).message}</Error_>;

  const o = consulta.data!;
  const abiertas = o.incidencias.filter((i) => !i.resuelta);
  const totalDestinos = o.lineas.reduce((n, l) => n + l.predistribucion.length, 0);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">{o.numero}</h1>
            <Pill estado={o.estado} />
          </div>
          <p className="mt-1 text-tenue">
            {o.clienteNombre} ·{' '}
            {o.canalOrigen === 'CORREO' ? 'llegó por correo' : `orden ${o.tipo} por EDI`} ·
            documento del {fecha(o.fechaDocumento)}
          </p>
        </div>
        {o.estado !== 'ANULADA' && (
          <BotonAnular
            onAnular={(motivo) => anular.mutate(motivo)}
            trabajando={anular.isPending}
          />
        )}
      </header>

      {o.estado === 'ANULADA' && <Aviso>Orden anulada: {o.anuladaMotivo}</Aviso>}
      {anular.isError && <Error_>{(anular.error as Error).message}</Error_>}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card titulo="Cabecera" className="lg:col-span-2">
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <Dato etiqueta="Ventana de entrega">
              {o.fechaEntregaMin === o.fechaEntregaMax ? (
                <>
                  {fecha(o.fechaEntregaMin)}{' '}
                  <span className="text-tenue">— fecha exacta, sin margen</span>
                </>
              ) : (
                <>
                  {fecha(o.fechaEntregaMin)} a {fecha(o.fechaEntregaMax)}
                </>
              )}
            </Dato>
            <Dato etiqueta="Plazo de pago">
              {o.plazoPagoDias ? `${o.plazoPagoDias} días desde la recepción de la factura` : '—'}
            </Dato>
            <Dato etiqueta="Lugar de entrega">
              {o.nombreDestino ??
                (o.glnDestino ? (
                  <NoSe crudo={o.glnDestino} que="Sede" />
                ) : (
                  <span className="text-tenue">{o.observaciones ?? '—'}</span>
                ))}
            </Dato>
            {/* Solo el canal EDI trae GLN. En el canal correo no hay, y una
                fila vacia diciendo «GLN» confunde mas que no mostrarla. */}
            {o.glnDestino && (
              <Dato etiqueta="GLN del destino">
                <span className="font-mono text-xs">{o.glnDestino}</span>
              </Dato>
            )}
            {o.ventanaHorariaInicio && (
              <Dato etiqueta="Ventana horaria">
                {o.ventanaHorariaInicio} a {o.ventanaHorariaFin ?? '?'}{' '}
                <span className="text-tenue">— la fija el cliente en el correo</span>
              </Dato>
            )}
            {o.observaciones && <Dato etiqueta="Observaciones">{o.observaciones}</Dato>}
          </dl>

          {o.notasCliente && <NotasDelCliente orden={o} />}
        </Card>

        <Card titulo="Valores">
          <dl className="space-y-2 text-sm">
            <Fila etiqueta="Pedido" valor={pesos(o.valorPedido)} fuerte />
            <Fila
              etiqueta="Recibido"
              valor={pesos(o.valorRecibido)}
              nota={o.valorRecibido === null ? 'todavía sin registrar' : undefined}
              fuerte={o.valorRecibido !== null}
            />
            <Fila
              etiqueta="Total impreso en el PDF"
              valor={pesos(o.valorTotalPdf)}
              nota={
                o.valorTotalPdf === null
                  ? 'no llegó el PDF'
                  : o.valorTotalPdf === o.valorPedido
                    ? 'cuadra con el calculado'
                    : 'NO cuadra con el calculado'
              }
            />
          </dl>
        </Card>
      </div>

      <PanelAgenda orden={o} />

      <PanelRecepcion orden={o} />

      {/*
        Debajo de la recepcion, y no encima: se lee en el orden en que pasan las
        cosas. Primero que llego, despues que volvio.
      */}
      <PanelDevoluciones orden={o} />

      {abiertas.length > 0 && (
        <Card titulo={`Por revisar (${abiertas.length})`}>
          <ul className="space-y-3">
            {abiertas.slice(0, 8).map((i) => (
              <ItemIncidencia key={i.id} incidencia={i} ordenId={id} />
            ))}
          </ul>
          {abiertas.length > 8 && (
            <p className="mt-3 text-sm text-tenue">
              y {abiertas.length - 8} más del mismo tipo.
            </p>
          )}
        </Card>
      )}

      <section>
        <h2 className="mb-2 text-base font-semibold">
          Líneas ({o.lineas.length}) y predistribución ({totalDestinos} destinos)
        </h2>
        <div className="space-y-4">
          {o.lineas.map((l) => (
            <LineaConPredistribucion key={l.id} linea={l} />
          ))}
        </div>
      </section>

      <Card titulo="Qué le ha pasado a esta orden">
        <ol className="space-y-2 text-sm">
          {agrupar(o.eventos).map((g) => (
            <li key={g.primero.id} className="flex gap-3 border-l-2 border-borde pl-3">
              <span className="w-36 shrink-0 text-xs text-tenue">
                {new Date(g.primero.ocurrioEn).toLocaleString('es-CO', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
              </span>
              <span>
                <span className="font-medium">
                  {g.primero.tipo.replaceAll('_', ' ').toLowerCase()}
                </span>
                {g.cuantos > 1 && (
                  <span className="text-tenue"> · {g.cuantos} veces</span>
                )}
                {' · '}
                {g.primero.descripcion}
                {g.primero.usuario && (
                  <span className="text-tenue"> — {g.primero.usuario}</span>
                )}
              </span>
            </li>
          ))}
        </ol>
      </Card>

      <Card titulo={`Documentos crudos (${o.archivos.length})`}>
        <ul className="space-y-1 text-sm">
          {o.archivos.map((a) => (
            <li key={a.id} className="flex flex-wrap items-baseline justify-between gap-2">
              <button
                type="button"
                onClick={() => void descargarArchivo(o.id, a.id, a.nombreOriginal)}
                className="font-mono text-xs text-acento underline hover:no-underline"
              >
                {a.nombreOriginal}
              </button>
              <span className="text-tenue">
                {a.formato} · {new Date(a.cargadoEn).toLocaleDateString('es-CO')}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-tenue">
          El documento original se guarda siempre. El día que el CEN cambie un campo, se reprocesa
          sin volver a descargarlo.
        </p>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Agrupa eventos seguidos del mismo tipo.
 *
 * Una orden con 25 destinos sin mapear produce 25 eventos identicos, y esa
 * repeticion ahoga lo que de verdad importa de la linea de tiempo: cuando
 * llego, cuando se adjunto el PDF, cuando se resolvio algo. Se muestra el
 * primero con la cuenta al lado.
 */
function agrupar(
  eventos: readonly EventoOrden[],
): Array<{ primero: EventoOrden; cuantos: number }> {
  const grupos: Array<{ primero: EventoOrden; cuantos: number }> = [];
  for (const e of eventos) {
    const ultimo = grupos.at(-1);
    if (ultimo && ultimo.primero.tipo === e.tipo && ultimo.primero.descripcion === e.descripcion) {
      ultimo.cuantos += 1;
    } else if (ultimo && ultimo.primero.tipo === e.tipo && e.tipo === 'INCIDENCIA_ABIERTA') {
      // Las incidencias del mismo tipo cambian solo en el GLN o la linea: se
      // cuentan juntas y el detalle completo esta arriba, en «Por revisar».
      ultimo.cuantos += 1;
    } else {
      grupos.push({ primero: e, cuantos: 1 });
    }
  }
  return grupos;
}

/**
 * Las notas del cliente, tal cual las escribieron.
 *
 * **El sistema no las interpreta** (D-80). Si la orden tiene ventana horaria
 * estructurada y la nota tambien menciona horas, se avisa y se muestran las
 * dos: **quien decide cual manda es la persona que las lee** (D-82). Calcular
 * «la mas restrictiva» exigiria parsear la nota, que es justo lo que no se hace.
 */
function NotasDelCliente({ orden }: { orden: OrdenDetalle }): ReactNode {
  const notas = orden.notasCliente ?? '';
  // Solo se detecta que HAY horas, no cuales son: eso ya seria interpretarlas.
  const mencionaHoras = /\d{1,2}\s*:\s*\d{2}/.test(notas);
  const hayConflicto = mencionaHoras && orden.ventanaHorariaInicio !== null;

  return (
    <div className="mt-4 border-t border-borde pt-3">
      <div className="text-xs text-tenue">Notas del cliente, tal cual</div>
      <p className="mt-1 whitespace-pre-wrap font-mono text-xs">{notas}</p>

      {hayConflicto && (
        <div className="mt-2 rounded-md border border-aviso/30 bg-aviso-tenue px-3 py-2 text-xs text-aviso">
          <strong>Léelas antes de programar.</strong> La orden trae una ventana horaria de{' '}
          {orden.ventanaHorariaInicio} a {orden.ventanaHorariaFin ?? '?'}, y la nota también
          menciona horas. No sabemos cuál manda, así que el sistema no elige: decide quien lea
          las dos.
        </div>
      )}
    </div>
  );
}

function LineaConPredistribucion({ linea }: { linea: LineaOrden }): ReactNode {
  const sinResolver = linea.predistribucion.filter((d) => d.codigoDependencia === null).length;

  return (
    <div className="rounded-lg border border-borde bg-superficie">
      <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-borde p-3">
        <div>
          <span className="text-xs text-tenue">Línea {linea.numeroLinea}</span>
          <div className="font-medium">
            {linea.nombreProducto ?? (
              <NoSe crudo={linea.descripcionCliente ?? linea.ean ?? '—'} que="Producto" />
            )}
          </div>
          <div className="text-xs text-tenue">
            {linea.ean !== null ? (
              <>
                EAN {linea.ean} · PLU del cliente {linea.pluCliente ?? '—'}
              </>
            ) : (
              /* El canal correo no manda codigo: la descripcion es la unica
                 identificacion que existe, y se muestra tal cual la escribieron. */
              <>el cliente lo llama «{linea.descripcionCliente}»</>
            )}
          </div>
        </div>
        <dl className="flex gap-6 text-sm">
          <div>
            <dt className="text-xs text-tenue">Pedida</dt>
            <dd className="cifra font-medium">
              {conUnidad(linea.cantidadPedida, linea.unidad)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-tenue">Recibida</dt>
            <dd className="cifra text-tenue">{cantidad(linea.cantidadRecibida)}</dd>
          </div>
          <div>
            <dt className="text-xs text-tenue">
              Precio {linea.unidad === 'KG' ? 'por kilo' : 'por unidad'}
            </dt>
            <dd className="cifra">{pesos(linea.precioUnitario)}</dd>
          </div>
          <div>
            <dt className="text-xs text-tenue">Valor</dt>
            <dd className="cifra font-medium">{pesos(linea.valorLinea)}</dd>
          </div>
        </dl>
      </div>

      {linea.predistribucion.length > 0 && (
        <div className="p-3">
          <div className="mb-2 text-xs text-tenue">
            Predistribución: {linea.predistribucion.length} destinos
            {sinResolver > 0 && (
              <span className="text-aviso">
                {' '}
                · {sinResolver} sin identificar contra el calendario del CEDI
              </span>
            )}
          </div>
          <Tabla>
            <thead>
              <tr className="border-b border-borde text-left text-xs uppercase tracking-wide text-tenue">
                <th className="px-3 py-1.5 font-medium">Dependencia</th>
                <th className="px-3 py-1.5 font-medium">GLN</th>
                <th className="px-3 py-1.5 text-right font-medium">Cantidad</th>
              </tr>
            </thead>
            <tbody>
              {linea.predistribucion.map((d) => (
                <tr key={d.glnDestino} className="border-b border-borde last:border-0">
                  <td className="px-3 py-1.5">
                    {d.codigoDependencia ? (
                      <>
                        <span className="font-medium">{d.codigoDependencia}</span>
                        {d.nombreDependencia && (
                          <span className="text-tenue"> — {d.nombreDependencia}</span>
                        )}
                      </>
                    ) : (
                      <NoSe crudo="sin código" que="Dependencia" />
                    )}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs">{d.glnDestino}</td>
                  <td className="cifra px-3 py-1.5">{cantidad(d.cantidad)}</td>
                </tr>
              ))}
            </tbody>
          </Tabla>
        </div>
      )}
    </div>
  );
}

function ItemIncidencia({ incidencia, ordenId }: { incidencia: Incidencia; ordenId: string }): ReactNode {
  const cliente = useQueryClient();
  const [nota, setNota] = useState('');
  const [abierto, setAbierto] = useState(false);

  const resolver = useMutation({
    mutationFn: () => resolverIncidencia(incidencia.id, nota),
    onSuccess: () => {
      setAbierto(false);
      setNota('');
      void cliente.invalidateQueries({ queryKey: ['orden', ordenId] });
    },
  });

  const mensaje = String(incidencia.detalle['mensaje'] ?? '');
  const otros = Object.entries(incidencia.detalle).filter(([k]) => k !== 'mensaje');

  return (
    <li className="border-l-2 border-aviso pl-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium">
          {incidencia.codigo.replaceAll('_', ' ').toLowerCase()}
          {incidencia.numeroLinea !== null && (
            <span className="text-tenue"> · línea {incidencia.numeroLinea}</span>
          )}
        </span>
        <Boton tipo="secundario" onClick={() => setAbierto((v) => !v)}>
          {abierto ? 'Cancelar' : 'Resolver'}
        </Boton>
      </div>

      {mensaje && <p className="mt-1 text-sm text-tenue">{mensaje}</p>}

      {otros.length > 0 && (
        <dl className="mt-1 grid gap-x-4 text-xs sm:grid-cols-2">
          {otros.map(([k, v]) => (
            <div key={k} className="flex gap-2">
              <dt className="text-tenue">{k}:</dt>
              <dd className="font-mono">{v === null ? '—' : String(v)}</dd>
            </div>
          ))}
        </dl>
      )}

      {abierto && (
        <div className="mt-2 flex flex-wrap gap-2">
          <input
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="Qué se hizo para resolverlo"
            className="flex-1 rounded-md border border-borde px-2 py-1.5 text-sm"
          />
          <Boton
            onClick={() => resolver.mutate()}
            deshabilitado={nota.trim().length === 0 || resolver.isPending}
          >
            Guardar
          </Boton>
        </div>
      )}
      {resolver.isError && (
        <p className="mt-1 text-xs text-alerta">{(resolver.error as Error).message}</p>
      )}
    </li>
  );
}

function BotonAnular({
  onAnular,
  trabajando,
}: {
  onAnular: (motivo: string) => void;
  trabajando: boolean;
}): ReactNode {
  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState('');

  if (!abierto) {
    return (
      <Boton tipo="peligro" onClick={() => setAbierto(true)}>
        Anular
      </Boton>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        placeholder="Por qué se anula"
        className="rounded-md border border-borde px-2 py-1.5 text-sm"
        autoFocus
      />
      <Boton
        tipo="peligro"
        onClick={() => onAnular(motivo)}
        deshabilitado={motivo.trim().length < 4 || trabajando}
      >
        Confirmar
      </Boton>
      <Boton tipo="secundario" onClick={() => setAbierto(false)}>
        Cancelar
      </Boton>
    </div>
  );
}

function Dato({ etiqueta, children }: { etiqueta: string; children: ReactNode }): ReactNode {
  return (
    <div>
      <dt className="text-xs text-tenue">{etiqueta}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Fila({
  etiqueta,
  valor,
  nota,
  fuerte = false,
}: {
  etiqueta: string;
  valor: string;
  nota?: string;
  fuerte?: boolean;
}): ReactNode {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-tenue">
        {etiqueta}
        {nota && <span className="block text-xs">{nota}</span>}
      </dt>
      <dd className={`cifra ${fuerte ? 'text-base font-semibold' : ''}`}>{valor}</dd>
    </div>
  );
}
